import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const Szenen = ({ projectId }) => {
  const [scenes, setScenes] = useState([]);
  const [figures, setFigures] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [newScene, setNewScene] = useState({
    szene: '',
    beschreibung: '',
    hauptfiguren: [],
    nebenfiguren: []
  });
  // Neuer State für die Anzeige, wenn keine Figuren vorhanden sind
  const [noFiguresMessage, setNoFiguresMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });
  useEffect(() => {
    const loadSavedData = () => {
      if (projectId) {
        // Szenen laden
        const savedScenes = localStorage.getItem(`scenes_${projectId}`);
        if (savedScenes) {
          try {
            const parsedScenes = JSON.parse(savedScenes);
            if (parsedScenes && parsedScenes.length > 0) {
              setScenes(parsedScenes);
            }
          } catch (error) {
            console.error('Error loading scenes:', error);
          }
        }

        // Figuren laden - prüfe sowohl "characters_" als auch "figures_" Schlüssel
        let savedFigures = localStorage.getItem(`characters_${projectId}`);
        
        // Wenn unter "characters_" nichts gefunden wurde, versuche es mit "figures_"
        if (!savedFigures) {
          savedFigures = localStorage.getItem(`figures_${projectId}`);
        }
        
        if (savedFigures) {
          try {
            const parsedFigures = JSON.parse(savedFigures);
            if (parsedFigures && parsedFigures.length > 0) {
              setFigures(parsedFigures);
              setNoFiguresMessage('');
            } else {
              setNoFiguresMessage('Keine Figuren gefunden. Bitte fügen Sie zuerst Figuren hinzu.');
            }
          } catch (error) {
            console.error('Error loading figures:', error);
            setNoFiguresMessage('Fehler beim Laden der Figuren.');
          }
        } else {
          setNoFiguresMessage('Keine Figuren gefunden. Bitte fügen Sie zuerst Figuren hinzu.');
        }
      }
    };

    loadSavedData();
  }, [projectId]);

  useEffect(() => {
    if (projectId && scenes && scenes.length > 0) {
      localStorage.setItem(`scenes_${projectId}`, JSON.stringify(scenes));
    }
  }, [scenes, projectId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewScene(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCellEdit = (id, field, value) => {
    setScenes(prev => prev.map(scene => 
      scene.id === id ? { ...scene, [field]: value } : scene
    ));
    setEditingCell(null);
  };

  const handleFigurSelect = (e, type, sceneId = null) => {
    const selectedFigures = Array.from(e.target.selectedOptions, option => option.value);
    
    if (sceneId) {
      setScenes(prev => prev.map(scene =>
        scene.id === sceneId ? { ...scene, [type]: selectedFigures } : scene
      ));
    } else {
      setNewScene(prev => ({
        ...prev,
        [type]: selectedFigures
      }));
    }
  };

  const addScene = () => {
    if (newScene.szene && (newScene.hauptfiguren.length > 0 || newScene.nebenfiguren.length > 0)) {
      const sceneToAdd = { 
        ...newScene,
        id: Date.now() 
      };
      
      setScenes(prev => [...prev, sceneToAdd]);
      setNewScene({
        szene: '',
        beschreibung: '',
        hauptfiguren: [],
        nebenfiguren: []
      });
    } else {
      alert('Bitte geben Sie einen Szenennamen ein und wählen Sie mindestens eine Haupt- oder Nebenfigur aus.');
    }
  };

  const deleteScene = (id) => {
    if (window.confirm('Möchten Sie diese Szene wirklich löschen?')) {
      setScenes(prev => {
        const updatedScenes = prev.filter(scene => scene.id !== id);
  
        if (updatedScenes.length === 0) {
          localStorage.removeItem(`scenes_${projectId}`);
        } else {
          localStorage.setItem(`scenes_${projectId}`, JSON.stringify(updatedScenes));
        }
  
        return updatedScenes;
      });
    }
  };
  
  const exportToCSV = () => {
    const csv = Papa.unparse(scenes);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'szenen.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let data;
      if (file.name.endsWith('.csv')) {
        const text = await file.text();
        const results = Papa.parse(text, {
          header: true,
          skipEmptyLines: true
        });
        data = results.data;
      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, {
          type: 'array',
          dateNOS: true,
          cellDates: true
        });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        data = XLSX.utils.sheet_to_json(worksheet, {
          raw: false
        });
      }

      const newScenes = data
        .filter(scene => scene.szene || scene.Szene)
        .map(scene => ({
          id: Date.now() + Math.random(),
          szene: scene.szene || scene.Szene || '',
          beschreibung: scene.beschreibung || scene.Beschreibung || '',
          hauptfiguren: (scene.hauptfiguren || scene.Hauptfiguren || '')
            .toString()
            .split(',')
            .map(f => f.trim())
            .filter(f => f !== ''),
          nebenfiguren: (scene.nebenfiguren || scene.Nebenfiguren || '')
            .toString()
            .split(',')
            .map(f => f.trim())
            .filter(f => f !== '')
        }));

      setScenes(prev => [...prev, ...newScenes]);
      alert(`${newScenes.length} Szenen wurden erfolgreich importiert.`);

    } catch (error) {
      console.error('Fehler beim Datei-Import:', error);
      alert('Fehler beim Import der Datei. Bitte überprüfen Sie das Format.');
    }
    e.target.value = '';
  };
// Funktion zum Sortieren der Szenen
const sortScenes = (key) => {
  let direction = 'ascending';
  if (sortConfig.key === key && sortConfig.direction === 'ascending') {
    direction = 'descending';
  }
  
  setSortConfig({ key, direction });
  
  const sortedScenes = [...scenes].sort((a, b) => {
    if (a[key] < b[key]) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (a[key] > b[key]) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });
  
  setScenes(sortedScenes);
};
  // Neue Funktion zum Erstellen von Test-Figuren (nur für Entwicklungszwecke)
  const createTestFigures = () => {
    const testFigures = [
      { id: 1, figur: 'Romeo', beschreibung: 'Protagonist' },
      { id: 2, figur: 'Julia', beschreibung: 'Protagonistin' },
      { id: 3, figur: 'Mercutio', beschreibung: 'Romeos Freund' },
      { id: 4, figur: 'Tybalt', beschreibung: 'Antagonist' }
    ];
    
    localStorage.setItem(`characters_${projectId}`, JSON.stringify(testFigures));
    setFigures(testFigures);
    setNoFiguresMessage('');
    alert('Test-Figuren wurden erstellt.');
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Szenen</h2>
      
      {/* Anzeige von Fehlermeldungen oder Hinweisen */}
      {noFiguresMessage && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p>{noFiguresMessage}</p>
          <button 
            onClick={createTestFigures}
            className="mt-2 bg-blue-500 text-white p-1 rounded hover:bg-blue-600"
          >
            Test-Figuren erstellen (nur für Entwicklung)
          </button>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-4 mb-6">
        <input
          type="text"
          name="szene"
          value={newScene.szene}
          onChange={handleInputChange}
          placeholder="Szene"
          className="border p-2"
          required
        />
        <textarea
          name="beschreibung"
          value={newScene.beschreibung}
          onChange={handleInputChange}
          placeholder="Beschreibung"
          className="border p-2"
          rows="3"
        />
        
        <div>
          <label className="block mb-2">Hauptfiguren (Strg/Cmd halten für Mehrfachauswahl)</label>
          <select
            multiple
            name="hauptfiguren"
            value={newScene.hauptfiguren}
            onChange={(e) => handleFigurSelect(e, 'hauptfiguren')}
            className="border p-2 w-full"
            size={Math.min(5, figures.length || 1)}
          >
            {figures.length > 0 ? 
              figures.map((figure) => (
                <option key={figure.id} value={figure.figur}>
                  {figure.figur}
                </option>
              )) : 
              <option disabled>Keine Figuren verfügbar</option>
            }
          </select>
        </div>

        <div>
          <label className="block mb-2">Nebenfiguren (Strg/Cmd halten für Mehrfachauswahl)</label>
          <select
            multiple
            name="nebenfiguren"
            value={newScene.nebenfiguren}
            onChange={(e) => handleFigurSelect(e, 'nebenfiguren')}
            className="border p-2 w-full"
            size={Math.min(5, figures.length || 1)}
          >
            {figures.length > 0 ? 
              figures.map((figure) => (
                <option key={figure.id} value={figure.figur}>
                  {figure.figur}
                </option>
              )) : 
              <option disabled>Keine Figuren verfügbar</option>
            }
          </select>
        </div>

        <button
          onClick={addScene}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
          disabled={figures.length === 0}
        >
          Szene hinzufügen
        </button>
      </div>

      <div className="mb-4 flex space-x-2">
        <input
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileUpload}
          className="hidden"
          id="csvUpload"
        />
        <label 
          htmlFor="csvUpload" 
          className="bg-green-500 text-white p-2 rounded cursor-pointer hover:bg-green-600"
        >
          Excel importieren
        </label>
        <button
          onClick={exportToCSV}
          className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          disabled={scenes.length === 0}
        >
          Als CSV exportieren
        </button>
      </div>

      {scenes.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border">
            <thead>
              <tr className="bg-gray-200">
              <th 
                  className="border p-2 cursor-pointer hover:bg-gray-300" 
                  onClick={() => sortScenes('szene')}
                >
                  Szene {sortConfig.key === 'szene' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                </th>
                <th className="border p-2">Beschreibung</th>
                <th className="border p-2">Hauptfiguren</th>
                <th className="border p-2">Nebenfiguren</th>
                <th className="border p-2">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {scenes.map((scene) => (
                <tr key={scene.id}>
                  <td 
                    className="border p-2 cursor-pointer"
                    onClick={() => setEditingCell({ id: scene.id, field: 'szene' })}
                  >
                    {editingCell?.id === scene.id && editingCell?.field === 'szene' ? (
                      <input
                        type="text"
                        value={scene.szene}
                        onChange={(e) => handleCellEdit(scene.id, 'szene', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        className="w-full"
                      />
                    ) : scene.szene}
                  </td>
                  <td 
                    className="border p-2 cursor-pointer"
                    onClick={() => setEditingCell({ id: scene.id, field: 'beschreibung' })}
                  >
                    {editingCell?.id === scene.id && editingCell?.field === 'beschreibung' ? (
                      <textarea
                        value={scene.beschreibung}
                        onChange={(e) => handleCellEdit(scene.id, 'beschreibung', e.target.value)}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        className="w-full"
                        rows="3"
                      />
                    ) : scene.beschreibung}
                  </td>
                  <td 
                    className="border p-2 cursor-pointer"
                    onClick={() => setEditingCell({ id: scene.id, field: 'hauptfiguren' })}
                  >
                    {editingCell?.id === scene.id && editingCell?.field === 'hauptfiguren' ? (
                      <select
                        multiple
                        value={scene.hauptfiguren}
                        onChange={(e) => handleFigurSelect(e, 'hauptfiguren', scene.id)}
                        onBlur={() => setEditingCell(null)}
                        className="w-full"
                        size={Math.min(5, figures.length || 1)}
                      >
                        {figures.map((figure) => (
                          <option key={figure.id} value={figure.figur}>
                            {figure.figur}
                          </option>
                        ))}
                      </select>
                    ) : scene.hauptfiguren.join(', ')}
                  </td>
                  <td 
                    className="border p-2 cursor-pointer"
                    onClick={() => setEditingCell({ id: scene.id, field: 'nebenfiguren' })}
                  >
                    {editingCell?.id === scene.id && editingCell?.field === 'nebenfiguren' ? (
                      <select
                        multiple
                        value={scene.nebenfiguren}
                        onChange={(e) => handleFigurSelect(e, 'nebenfiguren', scene.id)}
                        onBlur={() => setEditingCell(null)}
                        className="w-full"
                        size={Math.min(5, figures.length || 1)}
                      >
                        {figures.map((figure) => (
                          <option key={figure.id} value={figure.figur}>
                            {figure.figur}
                          </option>
                        ))}
                      </select>
                    ) : scene.nebenfiguren.join(', ')}
                  </td>
                  <td className="border p-2">
                    <button
                      onClick={() => deleteScene(scene.id)}
                      className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center p-4 border rounded bg-gray-50">
          Keine Szenen vorhanden. Fügen Sie oben neue Szenen hinzu.
        </div>
      )}
    </div>
  );
};

export default Szenen;